import React, { useState, useRef } from "react";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useDisclosure,
  ModalCloseButton,
  Box,
  Divider,
  Flex,
  Grid,
  Heading,
  Input,
  Textarea,
  FormControl,
  FormLabel,
  Text,
  HStack,
  Select,
  Skeleton,
  Stack,
  useToast,
  Icon,
  IconButton,
  VStack,
} from "@chakra-ui/react";
import { AnimatePresence, motion } from "framer-motion";
import {
  FiArrowLeft,
  FiArrowRight,
  FiCheckCircle,
  FiFile,
  FiPaperclip,
  FiPlus,
  FiRotateCcw,
  FiUpload,
  FiX,
} from "react-icons/fi";

import { axiosClient } from "../client/http-client";
import { getAuthToken } from "../client/token-utils";
import { useMutation, useQuery } from "@tanstack/react-query";
import { MotionReveal, WizardProgress, WizardTitle } from "./ui";
import { cssTransition, duration, ease, reducedTransition, useReducedMotion } from "../theme";

interface CreateTicketModalProps {
  triggerRefetch: () => void;
  /**
   * Lets a page place the trigger elsewhere (e.g. the page band's actions slot).
   * Receives the modal's `onOpen`; when omitted the default "New ticket" button renders here.
   */
  renderTrigger?: (onOpen: () => void) => React.ReactNode;
  /** Optional lifted disclosure so a page can open the modal from more than one place. */
  disclosure?: Pick<ReturnType<typeof useDisclosure>, "isOpen" | "onOpen" | "onClose">;
}

/** Inline §7.0 error state for the modal's lookups (case categories, contacts) with a Retry. */
const LoadError = ({ title, onRetry }: { title: string; onRetry: () => void }) => (
  <Alert status="error" alignItems="flex-start" mt={4}>
    <AlertIcon />
    <Box flex="1">
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription>Check your connection and try again.</AlertDescription>
    </Box>
    <Button variant="ghost" size="sm" leftIcon={<FiRotateCcw />} onClick={onRetry}>
      Retry
    </Button>
  </Alert>
);

interface formData {
  caseCategory: string;
  caseType: string;
  contactDetails: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
  subject: string;
  message: string;
  supportingDocument: string;
  supportingDocumentType: string;
  fileName?: string;
}

const steps = [
  { key: "contact", title: "Contact", description: "Who we should reach" },
  { key: "details", title: "Details", description: "What happened" },
];

const MotionBox = motion(Box);

/** h3 (xs look) + hairline that groups a set of fields inside a step (same treatment as the wizards' StepGroup). */
const FieldGroup = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <Box as="section">
    <Heading as="h3" size="xs">
      {title}
    </Heading>
    <Divider mt={3} mb={5} />
    {children}
  </Box>
);

const CreateTicketModal: React.FC<CreateTicketModalProps> = ({
  triggerRefetch,
  renderTrigger,
  disclosure,
}) => {
  const internalDisclosure = useDisclosure();
  const { isOpen, onOpen, onClose } = disclosure ?? internalDisclosure;
  const [activeStep, setActiveStep] = useState(0);
  const fileRef = useRef<HTMLInputElement>(null);
  const firstFieldRef = useRef<HTMLSelectElement>(null);
  const reduce = useReducedMotion();
  const token = getAuthToken();
  const toast = useToast();

  const getContactsData = async () => {
    const { data } = await axiosClient.get("/complaints/validateCustomer", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return data.data.contactDetails;
  };

  const getCaseTypes = async () => {
    const { data } = await axiosClient.get("/complaints/getCaseTypes", {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return data.data;
  };

  // useQuery to get Contacts Data
  const {
    data: contactsData,
    isPending: isContactsPending,
    isError: isContactsError,
    refetch: refetchContacts,
  } = useQuery({
    queryKey: ["contacts"],
    queryFn: getContactsData,
  });

  // useQuery to get Case Types
  const {
    data: caseTypes,
    isPending: isCaseTypesPending,
    isError: isCaseTypesError,
    refetch: refetchCaseTypes,
  } = useQuery({
    queryKey: ["caseTypes"],
    queryFn: getCaseTypes,
  });

  // formData
  const [formData, setFormData] = useState<formData>({
    caseCategory: "",
    caseType: "",
    contactDetails: {
      id: "",
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
    },
    subject: "",
    message: "",
    supportingDocument: "",
    supportingDocumentType: "",
    fileName: "",
  });

  //  categoryCaseTypes
  const categoryCaseTypes = caseTypes?.[formData.caseCategory];

  // Move to the next step
  const nextStep = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep(activeStep + 1);
    }
  };

  // Move to the previous step
  const prevStep = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  //   convert the uploaded file to base64
  const convertToBase64 = (file: any) => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file);
      fileReader.onload = () => {
        resolve(fileReader.result);
      };
      fileReader.onerror = (error) => {
        reject(error);
      };
    });
  };

  const AddTicket = () => {
    const data = axiosClient.post("/complaints/createTicket", formData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    return data;
  };

  const { isPending, mutate } = useMutation({
    mutationKey: ["ticket"],
    mutationFn: AddTicket,
    onSuccess: () => {
      triggerRefetch();
      onClose();
      // clear form data
      setFormData({
        caseCategory: "",
        caseType: "",
        contactDetails: {
          id: "",
          firstName: "",
          lastName: "",
          email: "",
          phone: "",
        },
        subject: "",
        message: "",
        supportingDocument: "",
        supportingDocumentType: "",
        fileName: "",
      });
      toast({
        title: "Ticket created.",
        description: "We've created your ticket for you.",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
    },
  });

  // form submission
  const handleSubmit = (e: any) => {
    e.preventDefault();
    mutate();
  };

  // Add validation functions
  const isStep1Valid = () => {
    return (
      formData.caseCategory !== "" &&
      formData.caseType !== "" &&
      formData.contactDetails.id !== ""
    );
  };

  const isStep2Valid = () => {
    return (
      formData.subject.trim() !== "" &&
      formData.message.trim() !== ""
    );
  };

  // The wizard rail fills to the end of a step once that step's fields validate (no save step here).
  const isCurrentStepComplete = activeStep === 0 ? isStep1Valid() : isStep2Valid();
  const hasFile = Boolean(formData.fileName);

  return (
    <>
      {renderTrigger ? (
        renderTrigger(onOpen)
      ) : (
        <Button
          variant="primary"
          leftIcon={<FiPlus />}
          onClick={onOpen}
          isLoading={isCaseTypesPending && isContactsPending && isPending}
        >
          New ticket
        </Button>
      )}
      <Modal
        size={"2xl"}
        isCentered
        onClose={onClose}
        isOpen={isOpen}
        motionPreset="slideInBottom"
        scrollBehavior="inside"
        initialFocusRef={firstFieldRef}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader p={0} border="none">
            <WizardTitle eyebrow="NEW TICKET" title="Raise a ticket" />
          </ModalHeader>
          <ModalCloseButton />
          <WizardProgress steps={steps} activeStep={activeStep} isStepSaved={isCurrentStepComplete} label="Ticket progress" />
          {/* When reopened past step 0 the initialFocusRef is empty: focus the current step's first field instead. */}
          <ModalBody data-autofocus-inside>
            <AnimatePresence exitBeforeEnter initial={false}>
              <MotionBox
                key={activeStep}
                initial={reduce ? false : { opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={reduce ? undefined : { opacity: 0, y: -6 }}
                transition={
                  reduce ? reducedTransition : { duration: duration.normal, ease: ease.standard }
                }
              >
                {activeStep === 0 ? (
                  <form>
                    <Stack spacing={6}>
                      <FieldGroup title="Case">
                        <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={5}>
                          <FormControl isRequired>
                            <FormLabel>Case category</FormLabel>
                            {/* Stays mounted while loading so initialFocusRef always resolves (§11). */}
                            <Select
                              ref={firstFieldRef}
                              placeholder={isCaseTypesPending ? "Loading categories…" : "Select a category"}
                              onChange={(e) =>
                                setFormData({
                                  ...formData,
                                  caseCategory: e.target.value,
                                })
                              }
                            >
                              {caseTypes
                                ? Object.entries(caseTypes).map(
                                    ([categoryId, category]: any) => (
                                      <option value={categoryId} key={categoryId}>
                                        {category.caseCategoryText}
                                      </option>
                                    )
                                  )
                                : null}
                            </Select>
                          </FormControl>

                          <FormControl isRequired>
                            <FormLabel>Case type</FormLabel>
                            <Select
                              placeholder={isCaseTypesPending ? "Loading case types…" : "Select a case type"}
                              onChange={(e) => {
                                setFormData({
                                  ...formData,
                                  caseType: e.target.value,
                                });
                              }}
                            >
                              {categoryCaseTypes
                                ? categoryCaseTypes.caseType.map(
                                    (caseType: any) => (
                                      <option
                                        value={caseType.value}
                                        key={caseType.value}
                                      >
                                        {caseType.text}
                                      </option>
                                    )
                                  )
                                : null}
                            </Select>
                          </FormControl>
                        </Grid>
                        {isCaseTypesError && !caseTypes && (
                          <LoadError
                            title="We couldn't load the case categories"
                            onRetry={() => refetchCaseTypes()}
                          />
                        )}
                      </FieldGroup>

                      <FieldGroup title="Contact">
                        <Stack spacing={5}>
                          <FormControl isRequired>
                            <FormLabel>Contact</FormLabel>
                            {isContactsPending ? (
                              <Skeleton speed={1.2} h="2.75rem" borderRadius="md" />
                            ) : isContactsError && !contactsData ? (
                              <LoadError title="We couldn't load your contacts" onRetry={() => refetchContacts()} />
                            ) : contactsData ? (
                              <Select
                                placeholder="Select a contact"
                                onChange={(e) => {
                                  const selectedContact = contactsData.find(
                                    (contact: any) =>
                                      contact.internalId.value === e.target.value
                                  );
                                  if (selectedContact) {
                                    setFormData({
                                      ...formData,
                                      contactDetails: {
                                        id: selectedContact.internalId.value,
                                        firstName: selectedContact.firstName.value,
                                        lastName: selectedContact.lastName.value,
                                        email: selectedContact.email.value,
                                        phone: selectedContact.phone.value,
                                      },
                                    });
                                  }
                                }}
                              >
                                {contactsData.map((contact: any, index: number) => (
                                  <option key={index} value={contact.internalId.value}>
                                    {`${contact.firstName.value} ${contact.lastName.value}`}
                                  </option>
                                ))}
                              </Select>
                            ) : null}
                          </FormControl>

                          <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={5}>
                            <FormControl>
                              <FormLabel>First name</FormLabel>
                              <Input value={formData.contactDetails.firstName} isReadOnly />
                            </FormControl>
                            <FormControl>
                              <FormLabel>Last name</FormLabel>
                              <Input value={formData.contactDetails.lastName} isReadOnly />
                            </FormControl>
                            <FormControl>
                              <FormLabel>Email</FormLabel>
                              <Input value={formData.contactDetails.email} isReadOnly />
                            </FormControl>
                            <FormControl>
                              <FormLabel>Phone number</FormLabel>
                              <Input value={formData.contactDetails.phone} isReadOnly />
                            </FormControl>
                          </Grid>
                        </Stack>
                      </FieldGroup>
                    </Stack>
                  </form>
                ) : activeStep === 1 ? (
                  <Stack spacing={5}>
                    <FormControl isRequired>
                      <FormLabel>Subject</FormLabel>
                      <Input
                        placeholder="Enter ticket subject"
                        value={formData.subject}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            subject: e.target.value,
                          })
                        }
                      />
                    </FormControl>

                    <FormControl isRequired>
                      <FormLabel>Message</FormLabel>
                      <Textarea
                        placeholder="Enter your message here..."
                        minH="9.5rem"
                        value={formData.message}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            message: e.target.value,
                          })
                        }
                      />
                    </FormControl>

                    <Box>
                      <Text textStyle="label" color="text.secondary" mb={2}>
                        Supporting document
                      </Text>
                      <Box
                        borderRadius="lg"
                        p={6}
                        border="1px dashed"
                        borderColor={hasFile ? "leaf.500" : "border.strong"}
                        bg={hasFile ? "leaf.50" : "sand.50"}
                        transition={cssTransition.colors}
                        _hover={{ borderColor: "leaf.500", bg: "leaf.50" }}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={async (e) => {
                          e.preventDefault();
                          const file = e.dataTransfer.files?.[0];
                          if (file) {
                            const base64 = await convertToBase64(file);
                            setFormData({
                              ...formData,
                              supportingDocument: base64 as string,
                              supportingDocumentType: file.type,
                              fileName: file.name,
                            });
                          }
                        }}
                      >
                        <Input
                          type="file"
                          ref={fileRef}
                          display="none"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              const base64 = await convertToBase64(file);
                              setFormData({
                                ...formData,
                                supportingDocument: base64 as string,
                                supportingDocumentType: file.type,
                                fileName: file.name,
                              });
                            }
                          }}
                        />

                        <VStack spacing={3}>
                          <Box
                            w="44px"
                            h="44px"
                            borderRadius="full"
                            bg="navy.50"
                            color="navy.700"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                            aria-hidden
                          >
                            <Icon as={FiPaperclip} boxSize="20px" />
                          </Box>
                          <Text textStyle="body.sm" fontWeight={600}>
                            Drag and drop files here
                          </Text>
                          <Text textStyle="caption" color="text.muted">
                            or
                          </Text>
                          <Button
                            variant="secondary"
                            size="sm"
                            leftIcon={<FiUpload />}
                            onClick={() => fileRef.current?.click()}
                          >
                            Browse files
                          </Button>

                          {formData.fileName && (
                            <MotionReveal y={4} style={{ width: "100%" }}>
                              <Flex
                                align="center"
                                justify="space-between"
                                gap={3}
                                w="full"
                                bg="surface.card"
                                border="1px solid"
                                borderColor="border.default"
                                borderRadius="md"
                                px={3}
                                py={2}
                              >
                                <HStack spacing={2} minW={0}>
                                  <Icon as={FiFile} boxSize="16px" color="leaf.700" aria-hidden />
                                  <Text
                                    textStyle="body.sm"
                                    fontWeight={600}
                                    noOfLines={1}
                                    title={formData.fileName}
                                  >
                                    {formData.fileName}
                                  </Text>
                                </HStack>
                                <IconButton
                                  aria-label="Remove file"
                                  icon={<FiX />}
                                  size="xs"
                                  variant="ghost"
                                  isRound
                                  onClick={() => setFormData({
                                    ...formData,
                                    supportingDocument: "",
                                    supportingDocumentType: "",
                                    fileName: "",
                                  })}
                                />
                              </Flex>
                            </MotionReveal>
                          )}
                        </VStack>
                      </Box>
                    </Box>
                  </Stack>
                ) : null}
              </MotionBox>
            </AnimatePresence>
          </ModalBody>
          <ModalFooter flexWrap="wrap">
            <Button
              variant="ghost"
              leftIcon={<FiArrowLeft />}
              onClick={prevStep}
              isDisabled={activeStep === 0}
            >
              Back
            </Button>
            {activeStep === steps.length - 1 ? (
              <Button
                variant="primary"
                leftIcon={<FiCheckCircle />}
                onClick={(e) => {
                  handleSubmit(e);
                }}
                isDisabled={!isStep2Valid()}
                isLoading={isPending}
              >
                Submit ticket
              </Button>
            ) : (
              <Button
                variant="primary"
                rightIcon={<FiArrowRight />}
                onClick={nextStep}
                isDisabled={!isStep1Valid()}
              >
                Next
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default CreateTicketModal;
